import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { fetchSince, fetchUnreadCount, markRead } from 'helpers/posNotifications'
import {
	ADD_POS_NOTIFICATIONS,
	MARK_POS_NOTIFICATIONS_READ,
	SET_POS_NOTIFICATIONS_UNREAD_COUNT,
} from 'store/posNotifications/actions'

const POLL_INTERVAL_MS = 15000
const DROPDOWN_LIMIT = 20

function labelKey(eventType) {
	return 'POS_NOTIFICATION_' + eventType
}

function formatTime(iso) {
	if (!iso) return ''
	try {
		const d = new Date(iso)
		const pad = (n) => (n < 10 ? '0' + n : '' + n)
		return pad(d.getHours()) + ':' + pad(d.getMinutes())
	} catch (e) {
		return ''
	}
}

function PosNotificationsBell() {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const posId = useSelector((s) => s.settings?.posId)
	const items = useSelector((s) => s.posNotifications?.items || [])
	const lastSeenId = useSelector((s) => s.posNotifications?.lastSeenId || 0)
	const unreadCount = useSelector((s) => s.posNotifications?.unreadCount || 0)

	const [open, setOpen] = useState(false)
	const wrapRef = useRef(null)
	const lastSeenRef = useRef(lastSeenId)
	useEffect(() => { lastSeenRef.current = lastSeenId }, [lastSeenId])

	const poll = useCallback(async () => {
		if (!posId) return
		if (document.hidden) return
		const list = await fetchSince(posId, lastSeenRef.current, 100)
		if (!Array.isArray(list) || list.length === 0) return
		dispatch(ADD_POS_NOTIFICATIONS(list))
		list.forEach((n) => {
			toast.info(t(labelKey(n.eventType), { defaultValue: n.eventType }))
			window.dispatchEvent(new CustomEvent('pos-notification', { detail: n }))
		})
	}, [posId, dispatch, t])

	useEffect(() => {
		if (!posId) return
		let cancelled = false;
		(async () => {
			const res = await fetchUnreadCount(posId)
			if (!cancelled && res && typeof res.count === 'number') {
				dispatch(SET_POS_NOTIFICATIONS_UNREAD_COUNT(res.count))
			}
		})()
		return () => { cancelled = true }
	}, [posId, dispatch])

	useEffect(() => {
		if (!posId) return
		poll()
		const id = setInterval(poll, POLL_INTERVAL_MS)
		return () => clearInterval(id)
	}, [posId, poll])

	useEffect(() => {
		const handler = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [])

	const visible = useMemo(() => items.slice(0, DROPDOWN_LIMIT), [items])

	const toggleOpen = async () => {
		const next = !open
		setOpen(next)
		if (next && posId) {
			const unreadIds = visible.filter((n) => !n.read).map((n) => n.id)
			if (unreadIds.length > 0) {
				dispatch(MARK_POS_NOTIFICATIONS_READ(unreadIds))
				markRead(posId, unreadIds)
			}
		}
	}

	if (!posId) return null

	return (
		<div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
			<button
				type="button"
				onClick={toggleOpen}
				className="btn header-item noti-icon waves-effect me-2 position-relative"
				style={{ padding: '10px', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', outline: 'none' }}
				title={t('POS_NOTIFICATIONS_TITLE', { defaultValue: 'POS activity' })}
			>
				<i className="uil-refresh position-relative" style={{ fontSize: '24px' }}>
					{unreadCount > 0 && (
						<span
							className="position-absolute bg-danger text-white rounded-circle d-flex align-items-center justify-content-center"
							style={{ top: '-5px', right: '-10px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold', border: '2px solid white' }}
						>
							{unreadCount > 99 ? '99+' : unreadCount}
						</span>
					)}
				</i>
			</button>
			{open && (
				<div
					className="position-absolute"
					style={{
						top: '50px', right: '0', minWidth: '300px', maxHeight: '420px',
						backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px',
						boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1050, overflowY: 'auto', color: '#000',
					}}
				>
					{visible.length === 0 ? (
						<div style={{ padding: '12px 16px', color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
							{t('POS_NOTIFICATIONS_EMPTY', { defaultValue: 'No notifications' })}
						</div>
					) : (
						visible.map((n) => (
							<div key={n.id} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
									<span>{t(labelKey(n.eventType), { defaultValue: n.eventType })}</span>
									<span style={{ color: '#888', fontSize: 12 }}>{formatTime(n.createdAt)}</span>
								</div>
								{n.actorLogin && (
									<div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>{n.actorLogin}</div>
								)}
							</div>
						))
					)}
				</div>
			)}
		</div>
	)
}

export default PosNotificationsBell
